import * as React from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { DocumentFormData, DocumentType, DocumentVisibility, DocumentCondition, DatePrecision } from '../types';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
// import { Switch } from '@/components/ui/switch'; // Not used currently
// import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'; // Not used currently
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { 
  AlertCircle, 
  // Calendar, // Icon, replaced by lucide-react Calendar
  FileText, 
  // Globe, // Icon, not used
  // History, // Icon, not used
  // Languages, // Icon, not used
  Link2, 
  // MapPin, // Icon, not used
  Tag, 
  User 
} from 'lucide-react';
// import { format } from 'date-fns'; // format is now handled by EnhancedDateInput or date-formatting utility
// import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'; // Handled by EnhancedDateInput
// import { Calendar as CalendarComponent } from '@/components/ui/calendar'; // Handled by EnhancedDateInput
// import { cn } from '@/lib/utils'; // cn is used within EnhancedDateInput
import { Badge } from '@/components/ui/badge';
import { EnhancedDateInput } from '@/components/ui/enhanced-date-input';
import { Label } from '@/components/ui/label';
import { DocumentFileManager } from './DocumentFileManager';


const documentTypeLabels: Record<DocumentType, string> = {
  article: 'Article',
  advertisement: 'Advertisement',
  editorial: 'Editorial',
  photo: 'Photograph',
  letter: 'Letter',
  document: 'Document',
  other: 'Other',
};

const documentTypeDescriptions: Record<DocumentType, string> = {
  article: 'A news article or written piece from a publication',
  advertisement: 'A printed advertisement or promotional material',
  editorial: 'An opinion piece or editorial from a publication',
  photo: 'A historical photograph or image',
  letter: 'A personal or official correspondence',
  document: 'An official document, report, or record',
  other: 'Other type of historical material',
};

const visibilityLabels: Record<DocumentVisibility, string> = {
  public: 'Public',
  private: 'Private',
  restricted: 'Restricted',
};

const visibilityDescriptions: Record<DocumentVisibility, string> = {
  public: 'Visible to all users and displayed in kiosk mode',
  private: 'Only visible to administrators and editors',
  restricted: 'Visible to specific user roles with appropriate permissions',
};

const conditionLabels: Record<DocumentCondition, string> = {
  excellent: 'Excellent',
  good: 'Good',
  fair: 'Fair',
  poor: 'Poor',
};

const conditionDescriptions: Record<DocumentCondition, string> = {
  excellent: 'No visible damage or deterioration',
  good: 'Minor wear but overall good condition',
  fair: 'Noticeable wear or damage but still intact',
  poor: 'Significant damage or deterioration',
};

// Form validation schema
const formSchema = z.object({
  title: z.string().min(3, {
    message: 'Title must be at least 3 characters.',
  }),
  description: z.string().optional(),
  document_type: z.enum(['article', 'advertisement', 'editorial', 'photo', 'letter', 'document', 'other']),
  original_date: z.string().min(4, { // YYYY is min, will be YYYY-MM-DD after EnhancedDateInput
    message: 'Date must include at least a year (YYYY).',
  }),
  original_date_precision: z.enum(['day', 'month', 'year', 'year-range', 'decade', 'circa']).optional(),
  date_start: z.number().optional(),
  date_end: z.number().optional(),
  date_type: z.enum(['single', 'range', 'decade', 'circa']).optional(),
  location: z.string().optional(),
  creator: z.string().optional(),
  source: z.string().min(1, {
    message: 'Source is required.',
  }),
  archive_source_location: z.string().min(1, {
    message: 'Archive source location is required.',
  }),
  historical_context: z.string().optional(),
  visibility: z.enum(['public', 'private', 'restricted']),
  publication_name: z.string().optional(),
  page_number: z.union([z.string(), z.number()]).optional(),
  section: z.string().optional(),
  language: z.string().min(2, {
    message: 'Language code is required (e.g., "en" for English).',
  }),
  entities: z.array(z.string()).optional(),
  tags: z.array(z.string()).optional(),
  categories: z.array(z.string()).optional(),
  preservation: z.object({
    source_archive: z.string().min(1, {
      message: 'Source archive is required.',
    }),
    condition: z.enum(['excellent', 'good', 'fair', 'poor']),
    archivist_notes: z.string().optional(),
    handling_restrictions: z.string().optional(),
  }),
  relations: z.object({
    related_articles: z.array(z.string()).optional(),
    event_id: z.string().optional(),
    person_id: z.array(z.string()).optional(),
    advertiser_id: z.string().optional(),
    collections: z.array(z.string()).optional(),
  }).optional(),
  metadata: z.record(z.string()).optional(),
});

type FormValues = {
  title: string;
  description?: string;
  document_type: 'article' | 'advertisement' | 'editorial' | 'photo' | 'letter' | 'document' | 'other';
  original_date: string;
  original_date_precision?: 'day' | 'month' | 'year' | 'year-range' | 'decade' | 'circa';
  date_start?: number;
  date_end?: number;
  date_type?: 'single' | 'range' | 'decade' | 'circa';
  location?: string;
  creator?: string;
  source: string;
  archive_source_location: string;
  historical_context?: string;
  visibility: 'public' | 'private' | 'restricted';
  publication_name?: string;
  page_number?: string | number;
  section?: string;
  language: string;
  entities?: string[];
  tags?: string[];
  categories?: string[];
  preservation: {
    source_archive: string;
    condition: 'excellent' | 'good' | 'fair' | 'poor';
    archivist_notes?: string;
    handling_restrictions?: string;
  };
  relations?: {
    related_articles?: string[];
    event_id?: string;
    person_id?: string[];
    advertiser_id?: string;
    collections?: string[];
  };
  metadata?: Record<string, string>;
};

interface DocumentFormProps {
  documentId?: string; // For existing documents
  initialData?: DocumentFormData;
  onSubmit: (data: DocumentFormData) => void;
  onCancel?: () => void;
  onFileUpload?: (file: File) => Promise<any>;
  uploadedFile?: {
    id: string;
    filename: string;
    storage_path: string;
    thumbnail_path: string | null;
    file_type: string;
    file_size: number;
    uploaded_at: string;
  } | null;
  isLoading?: boolean;
}

const minimalDefaultValues: Partial<FormValues> = {
  title: '',
  document_type: 'document',
  original_date: '',
  original_date_precision: 'day',
  source: '',
  archive_source_location: '',
  visibility: 'public',
  language: 'en',
  preservation: {
    source_archive: '',
    condition: 'good',
  },
  entities: [],
  tags: [],
  categories: [],
  relations: {
    related_articles: [],
    person_id: [],
    collections: [],
  },
  metadata: {},
};


export function DocumentForm({ documentId, initialData, onSubmit, onCancel, onFileUpload, uploadedFile, isLoading = false }: DocumentFormProps) {
  const [tagInput, setTagInput] = React.useState('');
  const [entityInput, setEntityInput] = React.useState('');
  const [categoryInput, setCategoryInput] = React.useState('');
  const [collectionInput, setCollectionInput] = React.useState('');
  const [relatedArticleInput, setRelatedArticleInput] = React.useState('');
  const [personIdInput, setPersonIdInput] = React.useState('');
  const [isUploading] = React.useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: (initialData || minimalDefaultValues) as Partial<FormValues>,
  });


  const selectedDocType = form.watch('document_type') as DocumentType;
  const selectedVisibility = form.watch('visibility') as DocumentVisibility;

  // Handle enhanced date input
  const handleEnhancedDateChange = (value: string, precision: DatePrecision) => {
    form.setValue('original_date', value);
    form.setValue('original_date_precision', precision);
    
    // Extract date_start, date_end, and date_type from the value and precision
    let dateStart: number | undefined = undefined;
    let dateEnd: number | undefined = undefined;
    let dateType: 'single' | 'range' | 'decade' | 'circa' = 'single';
    
    if (!value) {
      // Clear the fields if no value
      form.setValue('date_start', undefined);
      form.setValue('date_end', undefined);
      form.setValue('date_type', 'single');
      return;
    }
    
    try {
      switch (precision) {
        case 'day':
        case 'month':
        case 'year':
          // For single dates, extract the year
          const yearMatch = value.match(/^(\d{4})/);
          if (yearMatch) {
            const year = parseInt(yearMatch[1]);
            dateStart = year;
            dateEnd = year;
            dateType = 'single';
          }
          break;
          
        case 'year-range':
          // For year ranges like "1950-1960"
          const rangeMatch = value.match(/^(\d{4})-(\d{4})$/);
          if (rangeMatch) {
            dateStart = parseInt(rangeMatch[1]);
            dateEnd = parseInt(rangeMatch[2]);
            dateType = 'range';
          }
          break;
          
        case 'decade':
          // For decades like "1950s"
          const decadeMatch = value.match(/^(\d{4})s$/);
          if (decadeMatch) {
            const decadeStart = parseInt(decadeMatch[1]);
            dateStart = decadeStart;
            dateEnd = decadeStart + 9; // e.g., 1950s = 1950-1959
            dateType = 'decade';
          }
          break;
          
        case 'circa':
          // For circa dates like "circa 1955"
          const circaMatch = value.match(/^circa (\d{4})$/);
          if (circaMatch) {
            const year = parseInt(circaMatch[1]);
            dateStart = year;
            dateEnd = year;
            dateType = 'circa';
          }
          break;
          
        default:
          // Fallback: try to extract year from any format
          const fallbackMatch = value.match(/(\d{4})/);
          if (fallbackMatch) {
            const year = parseInt(fallbackMatch[1]);
            dateStart = year;
            dateEnd = year;
            dateType = 'single';
          }
          break;
      }
    } catch (error) {
      console.error('Error parsing date value:', error);
      // Fallback to single date type
      dateType = 'single';
    }
    
    // Set the calculated values
    form.setValue('date_start', dateStart);
    form.setValue('date_end', dateEnd);
    form.setValue('date_type', dateType);
    
    console.log('Date fields updated:', {
      original_date: value,
      original_date_precision: precision,
      date_start: dateStart,
      date_end: dateEnd,
      date_type: dateType
    });
  };

  // Handle tag input
  const handleAddTag = () => {
    if (tagInput.trim() !== '') {
      const currentTags = form.getValues('tags') || [];
      form.setValue('tags', [...currentTags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag: string) => {
    const currentTags = form.getValues('tags') || [];
    form.setValue('tags', currentTags.filter(t => t !== tag));
  };

  // Handle entity input
  const handleAddEntity = () => {
    if (entityInput.trim() !== '') {
      const currentEntities = form.getValues('entities') || [];
      form.setValue('entities', [...currentEntities, entityInput.trim()]);
      setEntityInput('');
    }
  };

  const handleRemoveEntity = (entity: string) => {
    const currentEntities = form.getValues('entities') || [];
    form.setValue('entities', currentEntities.filter(e => e !== entity));
  };

  // Handle category input
  const handleAddCategory = () => {
    if (categoryInput.trim() !== '') {
      const currentCategories = form.getValues('categories') || [];
      form.setValue('categories', [...currentCategories, categoryInput.trim()]);
      setCategoryInput('');
    }
  };

  const handleRemoveCategory = (category: string) => {
    const currentCategories = form.getValues('categories') || [];
    form.setValue('categories', currentCategories.filter(c => c !== category));
  };

  // Handle collection input
  const handleAddCollection = () => {
    if (collectionInput.trim() !== '') {
      const currentRelations = form.getValues('relations') || {};
      const currentCollections = currentRelations.collections || [];
      form.setValue('relations.collections', [...currentCollections, collectionInput.trim()]);
      setCollectionInput('');
    }
  };

  const handleRemoveCollection = (collection: string) => {
    const currentRelations = form.getValues('relations') || {};
    const currentCollections = currentRelations.collections || [];
    form.setValue('relations.collections', currentCollections.filter(c => c !== collection));
  };

  // Handle related article input
  const handleAddRelatedArticle = () => {
    if (relatedArticleInput.trim() !== '') {
      const currentRelations = form.getValues('relations') || {};
      const currentRelatedArticles = currentRelations.related_articles || [];
      form.setValue('relations.related_articles', [...currentRelatedArticles, relatedArticleInput.trim()]);
      setRelatedArticleInput('');
    }
  };

  const handleRemoveRelatedArticle = (article: string) => {
    const currentRelations = form.getValues('relations') || {};
    const currentRelatedArticles = currentRelations.related_articles || [];
    form.setValue('relations.related_articles', currentRelatedArticles.filter(a => a !== article));
  };

  // Handle person ID input
  const handleAddPersonId = () => {
    if (personIdInput.trim() !== '') {
      const currentRelations = form.getValues('relations') || {};
      const currentPersonIds = currentRelations.person_id || [];
      form.setValue('relations.person_id', [...currentPersonIds, personIdInput.trim()]);
      setPersonIdInput('');
    }
  };

  const handleRemovePersonId = (personId: string) => {
    const currentRelations = form.getValues('relations') || {};
    const currentPersonIds = currentRelations.person_id || [];
    form.setValue('relations.person_id', currentPersonIds.filter(p => p !== personId));
  };


  function handleSubmit(values: FormValues) {
    onSubmit(values);
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-8">
        <Tabs defaultValue="basic" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="basic">Basic Information</TabsTrigger>
            <TabsTrigger value="details">Document Details</TabsTrigger>
            <TabsTrigger value="metadata">Metadata & Tags</TabsTrigger>
            <TabsTrigger value="preservation">Preservation</TabsTrigger>
          </TabsList>

          {/* Basic Information Tab */}
          <TabsContent value="basic" className="space-y-4 pt-4">
            <div className="grid grid-cols-1 gap-4">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title</FormLabel>
                    <FormControl>
                      <Input placeholder="Document title" {...field} />
                    </FormControl>
                    <FormDescription>
                      The primary title or name of the document.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="Brief description of the document" 
                        className="min-h-[100px]"
                        {...field} 
                      />
                    </FormControl>
                    <FormDescription>
                      A concise description of the document's content.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="document_type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Document Type</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select document type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {Object.entries(documentTypeLabels).map(([type, label]) => (
                            <SelectItem key={type} value={type}>
                              {label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        {documentTypeDescriptions[selectedDocType]}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="space-y-2"> {/* Replaced FormField for original_date with direct EnhancedDateInput usage */}
                  <FormLabel>Original Date</FormLabel>
                  <EnhancedDateInput
                    value={form.watch('original_date')}
                    precision={form.watch('original_date_precision') as DatePrecision}
                    onValueChange={handleEnhancedDateChange}
                    placeholder="Select the original date of the document"
                  />
                  <FormDescription>
                    Choose the precision level and enter the original creation date of the document.
                  </FormDescription>
                  {/* Optionally, add FormMessage for original_date if needed, though EnhancedDateInput might handle its own validation display */}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="creator"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Creator</FormLabel>
                      <FormControl>
                        <Input placeholder="Author or creator" {...field} />
                      </FormControl>
                      <FormDescription>
                        The person or organization that created the document.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="location"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Location</FormLabel>
                      <FormControl>
                        <Input placeholder="Geographic location" {...field} />
                      </FormControl>
                      <FormDescription>
                        The geographic location associated with the document.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="source"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Source</FormLabel>
                      <FormControl>
                        <Input placeholder="Original source" {...field} />
                      </FormControl>
                      <FormDescription>
                        The original source of the document (e.g., collection, archive).
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="archive_source_location"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Archive Source Location</FormLabel>
                      <FormControl>
                        <Input placeholder="Box/Folder reference" {...field} />
                      </FormControl>
                      <FormDescription>
                        The specific location within the archive (e.g., Box 12, Folder 3).
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="visibility"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Visibility</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select visibility" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {Object.entries(visibilityLabels).map(([visibility, label]) => (
                          <SelectItem key={visibility} value={visibility}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      {visibilityDescriptions[selectedVisibility]}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DocumentFileManager
                documentId={documentId}
                uploadedFile={uploadedFile}
                onFileUpload={onFileUpload}
                isUploading={isUploading}
                isLoading={isLoading}
              />
            </div>
          </TabsContent>

          {/* Document Details Tab */}
          <TabsContent value="details" className="space-y-4 pt-4">
            <div className="grid grid-cols-1 gap-4">
              <FormField
                control={form.control}
                name="historical_context"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Historical Context</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="Historical significance and context" 
                        className="min-h-[150px]"
                        {...field} 
                      />
                    </FormControl>
                    <FormDescription>
                      Provide historical background and significance of this document.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="publication_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Publication Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Name of publication" {...field} />
                      </FormControl>
                      <FormDescription>
                        For articles, editorials, etc., the name of the publication.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="language"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Language</FormLabel>
                      <FormControl>
                        <Input placeholder="Language code (e.g., en)" {...field} />
                      </FormControl>
                      <FormDescription>
                        The primary language of the document (ISO code).
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="page_number"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Page Number</FormLabel>
                      <FormControl>
                        <Input placeholder="Page number" {...field} />
                      </FormControl>
                      <FormDescription>
                        For articles or documents from publications, the page number.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="section"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Section</FormLabel>
                      <FormControl>
                        <Input placeholder="Section of publication" {...field} />
                      </FormControl>
                      <FormDescription>
                        For articles, the section of the publication (e.g., Local News).
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-4">
                  <Link2 className="h-5 w-5 text-muted-foreground" />
                  <h3 className="text-lg font-medium">Related Items</h3>
                </div>
                <div className="space-y-4">
                  {/* Related Articles */}
                  <div>
                    <Label>Related Articles</Label>
                    <div className="flex gap-2 mt-1">
                      <Input 
                        placeholder="Article ID" 
                        value={relatedArticleInput}
                        onChange={(e) => setRelatedArticleInput(e.target.value)}
                      />
                      <Button 
                        type="button" 
                        variant="outline"
                        onClick={handleAddRelatedArticle}
                      >
                        Add
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {form.watch('relations.related_articles')?.map((article) => (
                        <Badge key={article} variant="secondary" className="gap-1">
                          {article}
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-4 w-4 p-0 hover:bg-transparent"
                            onClick={() => handleRemoveRelatedArticle(article)}
                          >
                            ×
                          </Button>
                        </Badge>
                      ))}
                    </div>
                  </div>

                  {/* Person IDs */}
                  <div>
                    <Label>Related People</Label>
                    <div className="flex gap-2 mt-1">
                      <Input 
                        placeholder="Person ID" 
                        value={personIdInput}
                        onChange={(e) => setPersonIdInput(e.target.value)}
                      />
                      <Button 
                        type="button" 
                        variant="outline"
                        onClick={handleAddPersonId}
                      >
                        Add
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {form.watch('relations.person_id')?.map((personId) => (
                        <Badge key={personId} variant="secondary" className="gap-1">
                          {personId}
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-4 w-4 p-0 hover:bg-transparent"
                            onClick={() => handleRemovePersonId(personId)}
                          >
                            ×
                          </Button>
                        </Badge>
                      ))}
                    </div>
                  </div>

                  {/* Event ID */}
                  <FormField
                    control={form.control}
                    name="relations.event_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Event ID</FormLabel>
                        <FormControl>
                          <Input placeholder="Related event ID" {...field} />
                        </FormControl>
                        <FormDescription>
                          ID of a related historical event.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Advertiser ID */}
                  <FormField
                    control={form.control}
                    name="relations.advertiser_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Advertiser ID</FormLabel>
                        <FormControl>
                          <Input placeholder="Advertiser ID (for advertisements)" {...field} />
                        </FormControl>
                        <FormDescription>
                          For advertisements, the ID of the advertiser.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Metadata & Tags Tab */}
          <TabsContent value="metadata" className="space-y-4 pt-4">
            <div className="grid grid-cols-1 gap-4">
              {/* Tags */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-4">
                  <Tag className="h-5 w-5 text-muted-foreground" />
                  <h3 className="text-lg font-medium">Tags</h3>
                </div>
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <Input 
                      placeholder="Add a tag" 
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                    />
                    <Button 
                      type="button" 
                      variant="outline"
                      onClick={handleAddTag}
                    >
                      Add
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {form.watch('tags')?.map((tag) => (
                      <Badge key={tag} variant="secondary" className="gap-1">
                        {tag}
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-4 w-4 p-0 hover:bg-transparent"
                          onClick={() => handleRemoveTag(tag)}
                        >
                          ×
                        </Button>
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>

              {/* Categories */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-4">
                  <FileText className="h-5 w-5 text-muted-foreground" />
                  <h3 className="text-lg font-medium">Categories</h3>
                </div>
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <Input 
                      placeholder="Add a category" 
                      value={categoryInput}
                      onChange={(e) => setCategoryInput(e.target.value)}
                    />
                    <Button 
                      type="button" 
                      variant="outline"
                      onClick={handleAddCategory}
                    >
                      Add
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {form.watch('categories')?.map((category) => (
                      <Badge key={category} variant="secondary" className="gap-1">
                        {category}
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-4 w-4 p-0 hover:bg-transparent"
                          onClick={() => handleRemoveCategory(category)}
                        >
                          ×
                        </Button>
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>

              {/* Entities */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-4">
                  <User className="h-5 w-5 text-muted-foreground" />
                  <h3 className="text-lg font-medium">Entities</h3>
                </div>
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <Input 
                      placeholder="Add an entity (person, place, organization)" 
                      value={entityInput}
                      onChange={(e) => setEntityInput(e.target.value)}
                    />
                    <Button 
                      type="button" 
                      variant="outline"
                      onClick={handleAddEntity}
                    >
                      Add
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {form.watch('entities')?.map((entity) => (
                      <Badge key={entity} variant="secondary" className="gap-1">
                        {entity}
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-4 w-4 p-0 hover:bg-transparent"
                          onClick={() => handleRemoveEntity(entity)}
                        >
                          ×
                        </Button>
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>

              {/* Collections */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-4">
                  <FileText className="h-5 w-5 text-muted-foreground" />
                  <h3 className="text-lg font-medium">Collections</h3>
                </div>
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <Input 
                      placeholder="Add a collection" 
                      value={collectionInput}
                      onChange={(e) => setCollectionInput(e.target.value)}
                    />
                    <Button 
                      type="button" 
                      variant="outline"
                      onClick={handleAddCollection}
                    >
                      Add
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {form.watch('relations.collections')?.map((collection) => (
                      <Badge key={collection} variant="secondary" className="gap-1">
                        {collection}
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-4 w-4 p-0 hover:bg-transparent"
                          onClick={() => handleRemoveCollection(collection)}
                        >
                          ×
                        </Button>
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Preservation Tab */}
          <TabsContent value="preservation" className="space-y-4 pt-4">
            <div className="grid grid-cols-1 gap-4">
              <FormField
                control={form.control}
                name="preservation.source_archive"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Source Archive</FormLabel>
                    <FormControl>
                      <Input placeholder="Source archive name" {...field} />
                    </FormControl>
                    <FormDescription>
                      The name of the archive or collection where the original document is stored.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="preservation.condition"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Condition</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select condition" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {Object.entries(conditionLabels).map(([condition, label]) => (
                          <SelectItem key={condition} value={condition}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      {conditionDescriptions[field.value as DocumentCondition]}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="preservation.archivist_notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Archivist Notes</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="Notes about the document's condition, preservation, etc." 
                        className="min-h-[100px]"
                        {...field} 
                      />
                    </FormControl>
                    <FormDescription>
                      Additional notes about the document's condition or preservation requirements.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="preservation.handling_restrictions"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Handling Restrictions</FormLabel>
                    <FormControl>
                      <Input placeholder="Special handling requirements" {...field} />
                    </FormControl>
                    <FormDescription>
                      Any special handling requirements or restrictions for the original document.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Preservation Information</AlertTitle>
                <AlertDescription>
                  This information is used to track the condition and handling requirements of the original physical document.
                </AlertDescription>
              </Alert>
            </div>
          </TabsContent>
        </Tabs>

        <div className="flex justify-end gap-2">
          <Button variant="outline" type="button" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? 'Saving...' : initialData ? 'Update Document' : 'Create Document'}
          </Button>
        </div>
      </form>
    </Form>
  );
}
